import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SearchableLookup.css';

/**
 * Campo de texto com lista pesquisável. A busca é feita no backend por `searchMethod(term)`,
 * que deve devolver uma lista de { value, description }. Ao abrir (foco) busca sem filtro;
 * conforme o usuário digita, busca novamente com o texto informado.
 */
function SearchableLookup({
    value,
    setAttribute,
    searchMethod,
    onSelect,
    onBlur,
    readOnly,
    disabled,
    placeholder,
    style,
    className = 'input-field',
    debounceMs = 300
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [position, setPosition] = useState(null);

    const inputRef = useRef(null);
    const listRef = useRef(null);
    const debounceRef = useRef(null);
    const requestRef = useRef(0);

    const blocked = readOnly || disabled;

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    // A lista fica num portal com position: fixed para não ser cortada por painéis com overflow
    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        const updatePosition = () => {
            if (!inputRef.current) {
                return;
            }
            const rect = inputRef.current.getBoundingClientRect();
            setPosition({ top: rect.bottom + 2, left: rect.left, minWidth: rect.width });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    useEffect(() => {
        if (highlightedIndex < 0 || !listRef.current) {
            return;
        }
        const item = listRef.current.children[highlightedIndex];
        if (item) {
            item.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex]);

    async function search(term) {
        const requestId = ++requestRef.current;
        setLoading(true);
        try {
            const result = await searchMethod(term);
            // Ignora respostas de buscas antigas que chegaram depois da mais recente
            if (requestId === requestRef.current) {
                setOptions(result || []);
                setHighlightedIndex(-1);
            }
        } finally {
            if (requestId === requestRef.current) {
                setLoading(false);
            }
        }
    }

    function open() {
        if (blocked) {
            return;
        }
        setIsOpen(true);
        search('');
    }

    function close() {
        clearTimeout(debounceRef.current);
        setIsOpen(false);
        setHighlightedIndex(-1);
    }

    function handleChange(event) {
        const text = event.target.value;
        setAttribute(text);
        setIsOpen(true);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(text), debounceMs);
    }

    function selectOption(option) {
        setAttribute(option.value);
        close();
        if (onSelect) {
            onSelect(option);
        }
        // Sai do campo para disparar o onBlur já com o valor selecionado
        setTimeout(() => inputRef.current && inputRef.current.blur(), 0);
    }

    function handleKeyDown(event) {
        if (!isOpen) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                open();
            }
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setHighlightedIndex(index => Math.min(index + 1, options.length - 1));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setHighlightedIndex(index => Math.max(index - 1, 0));
                break;
            case 'Enter':
                if (highlightedIndex >= 0 && options[highlightedIndex]) {
                    event.preventDefault();
                    selectOption(options[highlightedIndex]);
                }
                break;
            case 'Escape':
                close();
                break;
            default:
                break;
        }
    }

    function handleBlur(event) {
        close();
        if (onBlur) {
            onBlur(event);
        }
    }

    function getList() {
        if (!isOpen || !position) {
            return null;
        }

        let content;
        if (loading && options.length === 0) {
            content = <li className="searchable-lookup-message">Carregando...</li>;
        } else if (options.length === 0) {
            content = <li className="searchable-lookup-message">Nenhum resultado encontrado.</li>;
        } else {
            content = options.map((option, index) => (
                <li
                    key={`${option.value}-${index}`}
                    className={`searchable-lookup-option${index === highlightedIndex ? ' highlighted' : ''}`}
                    // mousedown + preventDefault evita que o input perca o foco antes da seleção
                    onMouseDown={(e) => { e.preventDefault(); selectOption(option); }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                >
                    <span className="searchable-lookup-value">{option.value}</span>
                    {option.description && <span className="searchable-lookup-description">{option.description}</span>}
                </li>
            ));
        }

        return createPortal(
            <ul
                ref={listRef}
                className="searchable-lookup-list"
                style={{ top: position.top, left: position.left, minWidth: position.minWidth }}
            >
                {content}
            </ul>,
            document.body
        );
    }

    return (
        <>
            <input
                ref={inputRef}
                className={className}
                type="text"
                autoComplete="off"
                value={value ?? ''}
                onChange={handleChange}
                onFocus={open}
                onClick={() => { if (!isOpen) open(); }}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                readOnly={readOnly}
                disabled={disabled}
                placeholder={placeholder}
                style={style}
            />
            {getList()}
        </>
    );
}

export default SearchableLookup;
